<script setup lang="ts">
import footerLogo from '#core/assets/img/icon/footer-logo.svg';
import { useAuthStore } from '#modules/auth/store/auth';

const config = useRuntimeConfig();
const appName = config.public.appName;
const socialUrls = config.public.socialUrls;

const reportAProblemLink = computed(() => {
  const authStore = useAuthStore();
  const { user } = storeToRefs(authStore);
  if (user.value) {
    return '/account-settings/report-a-problem';
  } else {
    return '/login';
  }
});
</script>

<template>
  <v-footer class="the-footer">
    <div class="footer-container">
      <!-- Top Section -->
      <div class="footer-top">
        <div class="footer-left">
          <img :src="footerLogo" :alt="appName" height="24" class="footer-logo" />
          <p class="footer-description">
            {{ appName }} is a platform to connect people and contractors. <br />
            We do not hire or manage contractors directly.
          </p>
        </div>

        <div class="footer-right">
          <h3 class="footer-section-title">Help</h3>
          <ul class="footer-links">
            <li><nuxt-link :href="'mailto:' + config.public?.appContactUsEmail">Contact Us</nuxt-link></li>
            <li><nuxt-link :to="reportAProblemLink">Report a Problem</nuxt-link></li>
          </ul>
        </div>
      </div>

      <!-- Bottom Section -->
      <div class="footer-bottom">
        <div class="footer-legal">
          <span class="footer-link" @click="$router.push('/terms')">Terms of Service</span>
          <span class="footer-link" @click="$router.push('/privacy')">Privacy Policy</span>
        </div>

        <div class="footer-social">
          <a :href="socialUrls.facebook" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
            <v-icon size="24" color="#1877F2">mdi-facebook</v-icon>
          </a>
          <a :href="socialUrls.x" target="_blank" rel="noopener noreferrer" aria-label="Twitter">
            <v-icon size="24" color="#000000">mdi-twitter</v-icon>
          </a>
          <a :href="socialUrls.instagram" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
            <v-icon size="24" color="#000000">mdi-instagram</v-icon>
          </a>
        </div>
      </div>
    </div>
  </v-footer>
</template>

<style lang="scss" scoped>
@use '#core/assets/scss/partials/colors' as *;
@use '#core/assets/scss/partials/fonts' as *;

.the-footer {
  background-color: white !important;
  color: #01011d;
  margin-top: auto;
  width: 100%;
  padding: 64px 80px;
}

.footer-container {
  width: 100%;
}

.footer-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 48px;
  gap: 48px;
}

.footer-left {
  flex: 1;
  max-width: 600px;

  .footer-logo {
    margin-bottom: 16px;
  }

  .footer-description {
    font-family: $font-family-primary;
    font-size: 14px;
    line-height: 24px;
    color: #80808e;
    margin: 0;
  }
}

.footer-center,
.footer-right {
  .footer-section-title {
    font-family: $font-family-primary;
    font-size: 16px;
    font-weight: 400;
    color: #80808e;
    margin: 0 0 16px 0;
  }

  .footer-links {
    list-style: none;
    padding: 0;
    margin: 0;

    li {
      margin-bottom: 8px;

      a {
        font-family: $font-family-primary;
        font-size: 16px;
        font-weight: 600;
        color: #01011d;
        text-decoration: none;
        transition: color 0.2s ease;
      }
    }
  }
}

.footer-bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 48px;
  border-top: 1px solid #e5e5e5;
}

.footer-legal {
  display: flex;
  gap: 32px;

  .footer-link {
    font-family: $font-family-primary;
    font-size: 16px;
    font-weight: 400;
    color: #80808e;
    cursor: pointer;
  }
}

.footer-social {
  display: flex;
  gap: 24px;
  align-items: center;

  a {
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.2s ease;
    text-decoration: none !important;

    &:hover {
      opacity: 0.7;
    }
  }
}

// Tablet: 768px
@media (max-width: 960px) {
  .the-footer {
    padding: 64px 80px;
  }

  .footer-top {
    flex-direction: column;
    gap: 48px;
    margin-bottom: 48px;
  }

  .footer-bottom {
    flex-direction: column;
    gap: 24px;
    align-items: flex-start;
    padding-top: 48px;
  }

  .footer-legal {
    flex-direction: column;
    gap: 16px;
  }
}

// Mobile: 375px
@media (max-width: 600px) {
  .the-footer {
    padding: 32px 16px;
  }

  .footer-top {
    gap: 24px;
    margin-bottom: 24px;
  }

  .footer-bottom {
    padding-top: 24px;
  }
}
</style>
